import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import WorkRequested from "./workRequested";
import Notes from "./notes";
import { VscAccount } from "react-icons/vsc";
import axios from "axios";
import "react-datepicker/dist/react-datepicker.css";
import "./PatientDetails.css";

const CustomDatePickerInput = ({ value, onClick }) => (
  <div className="custom-datepicker-wrapper" onClick={onClick}>
    <input
      type="text"
      value={value}
      placeholder="Select Date"
      readOnly
      className="patient-details-date-input"
    />
    <span className="patient-details-date-icon">📅</span>
  </div>
);

const PatientDetails = ({ account, onDataChange, formErrors, initialCaseData }) => {
  const [formData, setFormData] = useState({
    birth_date: new Date(),
    first_name: "",
    last_name: "",
    patientChart: "",
    selectedProvider: null,
    selectedGender: null,
    workRequested: {},
    notes: {},
  });

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const genders = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "transgender", label: "Transgender" },
  ];

  // Fetch contacts when account changes
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!account) {
          setContacts([]);
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/contacts/${account}`
        );

        const contactsData = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
          ? response.data
          : [];

        setContacts(contactsData);
        if (contactsData.length === 0) {
          setError("No providers found for this account.");
        }
      } catch (err) {
        console.error("Error fetching contacts:", err);
        setError("Failed to load providers. Please try again later.");
        setContacts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, [account]);

  // Prefill form data if initialCaseData is provided
  useEffect(() => {
    if (initialCaseData) {
      setFormData((prev) => ({
        ...prev,
        first_name: initialCaseData.first_name || "",
        last_name: initialCaseData.last_name || "",
        patientChart: initialCaseData.patientChart || "",
        birth_date: initialCaseData.birth_date ? new Date(initialCaseData.birth_date) : new Date(),
      }));
    }
  }, [initialCaseData]);

  // Transform contacts into provider options for Select component
  const providerOptions = React.useMemo(() => {
    const options = contacts.map((contact) => ({
      value: contact._id,
      label: `${contact.firstName} ${contact.lastName}`,
      originalData: contact,
    }));

    return options;
  }, [contacts]);

  // Set selectedProvider based on initialCaseData.provider_name after providerOptions are available
  useEffect(() => {
    if (initialCaseData && initialCaseData.provider_name && providerOptions.length > 0) {
      const matchingProvider = providerOptions.find(
        (option) => option.label === initialCaseData.provider_name
      );
      if (matchingProvider) {
        setFormData((prev) => ({ ...prev, selectedProvider: matchingProvider }));
      }
    }
  }, [initialCaseData, providerOptions]);

  // Notify parent component when form data changes
  useEffect(() => {
    onDataChange(formData);
  }, [formData, onDataChange]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProviderChange = (selectedProvider) => {
    setFormData((prev) => ({ ...prev, selectedProvider }));
  };

  const handleGenderChange = (selectedGender) => {
    setFormData((prev) => ({ ...prev, selectedGender }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, birth_date: date }));
  };

  const handleNotesDataChange = (data) => {
    setFormData((prev) => ({ ...prev, notes: data }));
  };

  const handleWorkRequestedDataChange = (data) => {
    setFormData((prev) => ({ ...prev, workRequested: data }));
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      padding: "clamp(1px, 0.1vw, 2px)",
      fontSize: "clamp(11px, 0.8vw, 14px)",
      borderRadius: "4px",
      borderColor: "#ced4da",
      marginBottom: "clamp(5px, 0.5vw, 10px)",
      minHeight: "clamp(28px, 2.5vw, 38px)",
    }),
    menu: (base) => ({
      ...base,
      fontSize: "clamp(11px, 0.8vw, 14px)",
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected ? "#004aad" : isFocused ? "#e6f0ff" : "white",
      color: isSelected ? "white" : "black",
      fontSize: "clamp(11px, 0.8vw, 14px)",
      padding: "clamp(3px, 0.3vw, 8px) clamp(5px, 0.5vw, 12px)",
    }),
  };

  return (
    <div className="patient-details-container">
      <div className="main-div">
        <h3 className="patient-details-header">
          <VscAccount />
          Patient Details
        </h3>
        <div className="patient-details-form">
          <div className="patient-details-row">
            {/* Left Section */}
            <div className="patient-details-column">
              <label className="patient-details-label">
                Provider <span className="required-field">*</span>
              </label>
              <Select
                classNamePrefix="rs"
                options={providerOptions}
                value={formData.selectedProvider}
                onChange={handleProviderChange}
                styles={selectStyles}
                placeholder={
                  loading ? "Loading providers..." : "Select Provider"
                }
                isLoading={loading}
                isDisabled={loading || !!error}
                className={formErrors.provider ? "is-invalid" : ""}
                noOptionsMessage={() =>
                  error ? error : "No providers available"
                }
              />
              {formErrors.provider && (
                <div className="error-message">
                  {formErrors.provider}
                </div>
              )}

              <label className="patient-details-label">First Name</label>
              <input
                type="text"
                value={formData.first_name}
                name="first_name"
                placeholder="Enter first name"
                onChange={handleFormChange}
                className="patient-details-input"
              />

              <label className="patient-details-label">
                Last Name <span className="required-field">*</span>
              </label>
              <input
                type="text"
                value={formData.last_name}
                name="last_name"
                placeholder="Enter last name"
                onChange={handleFormChange}
                className={`patient-details-input ${formErrors.lastName ? "is-invalid" : ""}`}
              />
              {formErrors.lastName && (
                <div className="error-message">
                  {formErrors.lastName}
                </div>
              )}
            </div>

            {/* Right Section */}
            <div className="patient-details-column">
              <label className="patient-details-label">Gender</label>
              <Select
                classNamePrefix="rs"
                options={genders}
                value={formData.selectedGender}
                onChange={handleGenderChange}
                styles={selectStyles}
                placeholder="Select Gender"
              />

              <label className="patient-details-label">Patient Chart</label>
              <input
                type="text"
                value={formData.patientChart}
                name="patientChart"
                placeholder="Enter chart number"
                onChange={handleFormChange}
                className={`patient-details-input ${formErrors.patientChart ? "is-invalid" : ""}`}
              />
              {formErrors.patientChart && (
                <div className="error-message">
                  {formErrors.patientChart}
                </div>
              )}

              <label className="patient-details-label">Birth Date</label>
              <DatePicker
                selected={formData.birth_date}
                onChange={handleDateChange}
                customInput={<CustomDatePickerInput />}
                name="birth_date"
                dateFormat="dd/MM/yyyy"
                maxDate={new Date()}
              />
            </div>
          </div>
        </div>

        <br />
        <WorkRequested
          onFormSubmit={handleWorkRequestedDataChange}
          formErrors={formErrors}
          account={account}
        />
        <br />
        <Notes onDataChange={handleNotesDataChange} />
      </div>
    </div>
  );
};

export default PatientDetails;